import React, { useCallback, useEffect, useRef, useState, FC } from 'react';
import { GET_COMMENTS_QUERY } from '@/lib/queries/post/comments';
import Comment from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/Comment';
import ReviewReply from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/ReviewReply';
import { countCommentReplies } from '@/utils/post/comments/countCommentReplies';
import ReviewAllReplies from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/ReviewAllReplies';
import { fetchQuery } from '@/utils/fetchQuery';
import { BrokerNode } from '@/stores/selectBrokers/store';
interface CommentReply {
    databaseId: number;
    replies?: {
        nodes: CommentReply[];
    };
    // Add other fields as necessary
}

interface CommentNode {
    databaseId: number;
    parent: number | null;
    commentedOn: {
        node: {
            databaseId: number;
        };
    };
    replies: {
        nodes: CommentReply[];
    };
    // Add other fields as necessary
}

interface CommentsData {
    comments: {
        nodes: CommentNode[];
        pageInfo: {
            hasNextPage: boolean;
            endCursor: string;
        };
    };
}

interface InfiniteScrollReviewsProps {
    postId: number[];
    brokers: BrokerNode[];
}

const InfiniteScrollReviews: FC<InfiniteScrollReviewsProps> = ({ postId, brokers = [] }) => {
    const [comments, setComments] = useState<CommentNode[]>([]);
    const [after, setAfter] = useState<string>('');
    const [gettingData, setGettingData] = useState<boolean>(false);
    const scrollingRef = useRef<HTMLDivElement | null>(null);
    const prevScrollPosition = useRef<number>(0);
    const [hasNextPage, setHasNextPage] = useState<boolean>(true);
    const existingCommentIds = useRef<Set<number>>(new Set());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    useEffect(() => {
        const fetchInitialComments = async () => {
            try {
                const data: CommentsData = await fetchQuery(GET_COMMENTS_QUERY, {
                    postId: postId,
                    first: 10,
                    after: '',
                });

                setComments(data.comments.nodes);
                setAfter(data.comments.pageInfo.endCursor);
                setHasNextPage(data.comments.pageInfo.hasNextPage);
                existingCommentIds.current = new Set(
                    data.comments.nodes.map((comment) => comment.databaseId),
                );
                setLoading(false);
            } catch (err) {
                setError(err);
                setLoading(false);
            }
        };

        fetchInitialComments();
    }, [postId]);

    const fetchMoreComments = useCallback(async () => {
        setGettingData(true);

        try {
            if (hasNextPage) {
                const data: CommentsData = await fetchQuery(GET_COMMENTS_QUERY, {
                    postId: postId,
                    first: 10,
                    after,
                });

                const filteredNewComments = data.comments.nodes.filter((comment) => {
                    if (!existingCommentIds.current.has(comment.databaseId)) {
                        existingCommentIds.current.add(comment.databaseId);
                        return true;
                    } else {
                        return false;
                    }
                });

                setComments((prevComments) => [...prevComments, ...filteredNewComments]);
                setAfter(data.comments.pageInfo.endCursor);
                setHasNextPage(data.comments.pageInfo.hasNextPage);
            }
        } catch (error) {
            console.error('Error fetching more comments:', error);
        } finally {
            setGettingData(false);
        }
    }, [after, hasNextPage, postId]);

    useEffect(() => {
        const scrollingElement = scrollingRef.current;

        if (!scrollingElement) return;

        const handleScroll = () => {
            const scrollPosition = scrollingElement.scrollTop;
            const scrollHeight = scrollingElement.scrollHeight;
            const clientHeight = scrollingElement.clientHeight;

            const scrollPercentage = (scrollPosition / (scrollHeight - clientHeight)) * 100;

            if (
                scrollPercentage >= 95 &&
                !gettingData &&
                scrollPosition > prevScrollPosition.current
            ) {
                prevScrollPosition.current = scrollPosition;
                fetchMoreComments();
            }
        };

        scrollingElement.addEventListener('scroll', handleScroll);

        return () => {
            scrollingElement.removeEventListener('scroll', handleScroll);
        };
    }, [after, gettingData, fetchMoreComments]);

    const handleClick = () => {
        fetchMoreComments();
    };

    if (loading)
        return (
            <div className='absolute bottom-3 right-0 flex w-full animate-pulse justify-center text-center'>
                <svg
                    className='size-5 animate-spin'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                >
                    <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                    ></circle>
                    <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                </svg>
            </div>
        );
    if (error) {
        console.error('Error fetching comments:', error);
        return <div>Error loading comments</div>;
    }

    return (
        <div className='h-full overflow-y-auto' ref={scrollingRef}>
            {comments.map((comment) => (
                <div
                    key={comment.databaseId}
                    className='mb-3 flex min-h-[160px] flex-col justify-between rounded-xl border border-gray-300 dark:border-white/15 p-5'
                >
                    <Comment
                        comment={comment}
                        broker={brokers.find(
                            (broker) => broker.databaseId === comment.commentedOn.node.databaseId,
                        )}
                    />
                    {comment.replies.nodes.length > 0 && (
                        <ReviewReply comment={comment.replies.nodes[0]} />
                    )}
                    {countCommentReplies(comment.replies.nodes) > 1 && (
                        <ReviewAllReplies comment={comment} />
                    )}
                </div>
            ))}
            {hasNextPage && !gettingData && comments.length > 0 && (
                <div
                    onClick={handleClick}
                    role='button'
                    className='bottom-0 mb-3 flex min-h-[60px] items-center justify-center rounded-xl border p-2 text-blue-500'
                >
                    نمایش بیشتر
                </div>
            )}
            {(gettingData || comments.length === 0) && (
                <div className='absolute bottom-3 right-0 flex w-full animate-pulse justify-center text-center'>
                    <svg
                        className='size-5 animate-spin'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                    >
                        <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                        ></circle>
                        <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                    </svg>
                </div>
            )}
        </div>
    );
};

export default InfiniteScrollReviews;
